import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { GetEventsFn } from '../../hooks/useCalendarSlots'
import { CalendarClient, GetBookingDetailsForClient } from '../../ui-models/clients/CalendarClient'
import { tID } from '../../utils/utils'
import { ConfirmationModal } from '../confirmationModal/ConfirmationModal'
import {
  ProviderCalendarScheduleSessionContent,
  ProviderCalendarScheduleSessionContentRef,
} from '../providerCalendarScheduleSessionContent/ProviderCalendarScheduleSessionContent'

export type ProviderCalendarScheduleSessionModalProps = {
  visible: boolean
  onRequestClose: () => void
  suggestedClients: CalendarClient[]
  clients: CalendarClient[]
  getEvents?: GetEventsFn
  slotMaxTime?: string
  slotMinTime?: string
  onAddSession?: (event: {
    client: string
    startTime: string
    endTime: string
    slotEndTime: string
    duration: number
    meetingFormat: string
    episode?: Episode
    address?: string
    videoPlatform?: VIDEO_PLATFORM
    sessionType?: string
  }) => Promise<void>
  getBookingDetailsForClient?: GetBookingDetailsForClient
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  timeZone?: string
  bookableInPersonLocations?: Address[] | null
  enableBackdatedAppointments?: boolean
  isSelfPayEnabled?: boolean
  isLegacyProvider?: boolean
  isLegacyClientListLoading?: boolean
  splitVideoOptions?: boolean
  providerVideoPlatform?: VIDEO_PLATFORM | null
  videoPreference?: boolean
  inPersonPreference?: boolean
}

export const ProviderCalendarScheduleSessionModal: React.FC<ProviderCalendarScheduleSessionModalProps> = ({
  visible,
  onRequestClose,
  suggestedClients,
  clients,
  getEvents,
  slotMaxTime = '22:00:00',
  slotMinTime = '07:00:00',
  onAddSession,
  getBookingDetailsForClient,
  rangeToAdd,
  timeZone,
  bookableInPersonLocations,
  enableBackdatedAppointments,
  isSelfPayEnabled,
  isLegacyProvider,
  isLegacyClientListLoading,
  splitVideoOptions,
  providerVideoPlatform,
  videoPreference,
  inPersonPreference,
}) => {
  const contentRef = useRef<ProviderCalendarScheduleSessionContentRef>(null)
  const [isLoading, setIsLoading] = useState(false)
  const handleAnimationEnd = () => {
    contentRef.current?.reset()
  }
  const confirmSelection = async () => {
    const values = contentRef.current?.submit()
    if (values) {
      setIsLoading(true)
      await onAddSession?.(values)
      setIsLoading(false)
      onRequestClose()
    }
  }
  return (
    <ConfirmationModal
      visible={visible}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onConfirmationButtonPress={confirmSelection}
      modalTitle={
        isLegacyProvider ? (
          <FormattedMessage
            defaultMessage='Schedule follow-up session'
            description='Title of modal to schedule a follow-up session for legacy providers'
          />
        ) : (
          <FormattedMessage defaultMessage='Schedule session' description='Title of modal to schedule a session' />
        )
      }
      confirmationButtonText={<FormattedMessage defaultMessage='Schedule' description='Button to schedule a session' />}
      cancelButtonText={
        <FormattedMessage defaultMessage='Cancel' description='Button to cancel scheduling a session' />
      }
      confirmButtonDisabled={false}
      confirmButtonLoading={isLoading}
      testID={tID('ProviderCalendarScheduleSessionModal')}
      modalContents={
        <ProviderCalendarScheduleSessionContent
          ref={contentRef}
          suggestedClients={suggestedClients}
          clients={clients}
          getEvents={getEvents}
          slotMaxTime={slotMaxTime}
          slotMinTime={slotMinTime}
          rangeToAdd={rangeToAdd}
          timeZone={timeZone}
          getBookingDetailsForClient={getBookingDetailsForClient}
          bookableInPersonLocations={bookableInPersonLocations}
          enableBackdatedAppointments={enableBackdatedAppointments}
          isSelfPayEnabled={isSelfPayEnabled}
          isLegacyProvider={isLegacyProvider}
          isLegacyClientListLoading={isLegacyClientListLoading}
          splitVideoOptions={splitVideoOptions}
          providerVideoPlatform={providerVideoPlatform}
          videoPreference={videoPreference}
          inPersonPreference={inPersonPreference}
        />
      }
    />
  )
}
